import Button, { ButtonProps } from '@carvana/ds-button';
import Skeleton from '@carvana/ds-skeleton';
import { icon } from '@carvana/ds-styles';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import cx from 'classnames';
import { Heart } from 'lucide-react';
import { useCallback, useContext } from 'react';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import { useSectionAnalytics } from '~/context/vdp/SectionAnalyticsContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleIdAndStockNumber } from '~/context/vdp/VehicleContext';
import { useToggleFavorite } from '~/hooks/vdp/favorites';

function useLoading() {
  const isLoggedIn = useIsLoggedIn();
  const {
    favorites: { hasEverLoaded }
  } = useContext(UserContext);

  return isLoggedIn && !hasEverLoaded;
}

type OwnProps = {
  size: ButtonProps['size'];
};

export default function Favorite({ size }: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { isFavorite } = useContext(UserVehicleContext);
  const { stockNumber, vehicleId } = useGetVehicleIdAndStockNumber();
  const isLoading = useLoading();
  const { analyticsAttributes, analyticsId } = useSectionAnalytics();
  const toggleFavorite = useToggleFavorite();

  const handleClick = useCallback(() => {
    if (analyticsId) {
      trackEvent(analyticsId, {
        ...analyticsAttributes,
        action: 'clicked',
        description: isFavorite ? 'favorite remove' : 'favorite add',
        vehicleId
      });
    }
    toggleFavorite({ stockNumber, vehicleId });
  }, [
    analyticsAttributes,
    analyticsId,
    isFavorite,
    stockNumber,
    toggleFavorite,
    trackEvent,
    vehicleId
  ]);

  return (
    <>
      {isLoading ? (
        <div className="flex h-2xl w-2xl items-center justify-center">
          <Skeleton width="24px" height="24px" className="m-0" />
        </div>
      ) : (
        <Button
          data-adobe-id={null}
          data-analytics-id={null}
          data-testid="vehicle-info-card-favorite"
          icon
          onClick={handleClick}
          size={size}
          title="Add to Favorites"
          variant="tertiary"
        >
          <Heart
            color={isFavorite ? icon.critical : 'currentColor'}
            className={cx(isFavorite ? 'fill-icon-critical' : '')}
          />
          <span className="sr-only">Add to Favorites</span>
        </Button>
      )}
    </>
  );
}
